import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  AccessTime as TimeoutIcon,
  Schedule as WaitingIcon,
  RocketLaunch as DispatchingIcon,
  Replay as ReplayIcon,
  OpenInNew as OpenInNewIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import { BatchStep, isTerminalStep } from '../../../models/SiteBatch';
import { buildRunURL } from '../../../config/github';
import siteBatchService from '../../../services/SiteBatchService';

/**
 * deploy-failure-github-link: 실패/타임아웃/진행중 상태에서 GH run_id 가
 * 있으면 GitHub Actions 로그 링크을 노출한다.
 */
function shouldShowRunLink(step: BatchStep): boolean {
  if (!step.github_run_id) return false;
  return step.status === 'failed' || step.status === 'timeout' || step.status === 'running';
}

interface SiteBatchRowProps {
  step: BatchStep;
  projectId: string;
  batchId: number;
  /** True when the batch is `completed` AND the step is failed/timeout. Drives retry button visibility. */
  canRetry: boolean;
  /** Retry request in flight for THIS step — disables the button to prevent double-click. */
  retrying: boolean;
  onRetry: (stepIndex: number) => void;
}

/**
 * Row in the SiteBatchProgressModal.
 * Renders the step's label, status badge, elapsed duration, optional ↗ GitHub link,
 * and inline log viewer (deploy-failure-inline-log).
 */
const SiteBatchRow: React.FC<SiteBatchRowProps> = ({
  step,
  projectId,
  batchId,
  canRetry,
  retrying,
  onRetry,
}) => {
  const theme = useTheme();
  const badge = statusBadge(step.status);
  const terminal = isTerminalStep(step.status);

  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState<string | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);

  const handleShowLogs = async () => {
    if (logsOpen) {
      setLogsOpen(false);
      return;
    }
    setLogsOpen(true);
    if (logs !== null) return; // already fetched
    setLogsLoading(true);
    try {
      const text = await siteBatchService.getStepLogs(projectId, batchId, step.index);
      setLogs(text || '(로그 없음)');
    } catch {
      setLogs('로그를 불러오지 못했습니다.');
    } finally {
      setLogsLoading(false);
    }
  };

  const rowBgColor = terminal && step.status !== 'success'
    ? alpha(theme.palette[badge.palette].main, 0.08)
    : 'background.default';

  return (
    <Box
      data-testid={`site-batch-row-${step.index}`}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: rowBgColor,
      }}
    >
      {/* ── Main grid row ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '32px 1fr auto auto',
          gap: 2,
          alignItems: 'center',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {step.index}
        </Typography>

        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {step.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {step.appname}
            {step.failure_reason && ` · ${step.failure_reason}`}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {!terminal && step.status !== 'waiting' && (
            <CircularProgress size={14} color={badge.palette} />
          )}
          <Chip
            size="small"
            color={badge.palette}
            variant={terminal ? 'filled' : 'outlined'}
            icon={badge.icon}
            label={badge.label}
          />
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 96, justifyContent: 'flex-end' }}>
          {shouldShowRunLink(step) && (
            <>
              <Tooltip title="GitHub Actions에서 로그 보기">
                <IconButton
                  size="small"
                  component="a"
                  href={buildRunURL(step.github_run_id!)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`GitHub Actions run ${step.github_run_id} 로그`}
                  data-testid={`site-batch-gh-link-${step.index}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={logsOpen ? '로그 닫기' : '로그 보기'}>
                <IconButton
                  size="small"
                  onClick={handleShowLogs}
                  color={logsOpen ? 'primary' : 'default'}
                  aria-label={logsOpen ? '로그 닫기' : '로그 보기'}
                  data-testid={`site-batch-log-toggle-${step.index}`}
                >
                  {logsLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <TerminalIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}
          {canRetry ? (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<ReplayIcon />}
              onClick={() => onRetry(step.index)}
              disabled={retrying}
              data-testid={`site-batch-retry-${step.index}`}
            >
              {retrying ? '재배포 중' : '재배포'}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {formatDuration(step)}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* ── Inline log panel ── */}
      <Collapse in={logsOpen}>
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
            p: 1.5,
            maxHeight: 320,
            overflow: 'auto',
          }}
        >
          {logsLoading ? (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={20} />
            </Stack>
          ) : (
            <Typography
              component="pre"
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                color: theme.palette.mode === 'dark' ? 'grey.100' : 'grey.900',
                m: 0,
                lineHeight: 1.6,
              }}
            >
              {logs}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

type BadgePalette = 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info';

interface Badge {
  label: string;
  palette: 'primary' | 'success' | 'error' | 'warning' | 'info';
  icon?: React.ReactElement;
}

function statusBadge(status: BatchStep['status']): Badge {
  switch (status) {
    case 'waiting':
      return { label: '대기', palette: 'info', icon: <WaitingIcon fontSize="small" /> };
    case 'dispatching':
      return { label: '시작 중', palette: 'primary', icon: <DispatchingIcon fontSize="small" /> };
    case 'running':
      return { label: '배포 중', palette: 'primary' };
    case 'success':
      return { label: '성공', palette: 'success', icon: <CheckIcon fontSize="small" /> };
    case 'failed':
      return { label: '실패', palette: 'error', icon: <CancelIcon fontSize="small" /> };
    case 'timeout':
      return { label: '시간 초과', palette: 'warning', icon: <TimeoutIcon fontSize="small" /> };
    case 'skipped':
      return { label: '건너뜀', palette: 'info' };
    default:
      return { label: status, palette: 'info' };
  }
}

function formatDuration(step: BatchStep): string {
  if (step.duration_sec != null && step.duration_sec >= 0) {
    const m = Math.floor(step.duration_sec / 60);
    const s = step.duration_sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }
  if (step.dispatched_at) {
    const d = new Date(step.dispatched_at);
    if (!Number.isNaN(d.getTime())) {
      const elapsed = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
      const m = Math.floor(elapsed / 60);
      const s = elapsed % 60;
      return m > 0 ? `${m}m ${s}s` : `${s}s`;
    }
  }
  return '—';
}

// Keep unused type reference silent in strict TS configs.
export type { BadgePalette };
export default SiteBatchRow;
